import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { useFormWithNotification } from "@covertree/hooks";
import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import { Modal } from "@covertree/ui";

import { EmailValidationModal } from "./EmailValidationModal";
import { UpdateContactInfoForm } from "./UpdateContactInfoForm";
import { EditContactForm, contactValidation } from "./form";
import { useUpdatePolicyholder } from "./useUpdatePolicyholder";
import { BulkEsignRemindersStatusesModal } from "./BulkEsignRemindersStatusesModal";

interface Props {
  defaultValues: EditContactForm;
  open: boolean;
  policyholderLocator: string;
  onClose: () => void;
}

export function UpdateContactInfoModal({
  defaultValues,
  open,
  policyholderLocator,
  onClose,
}: Props) {
  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    delayError: FORM_ERROR_DELAY_IN_MS,
    defaultValues,
    resolver: yupResolver(contactValidation(defaultValues.noEmail)),
  });

  const handleClose = useCallback(() => {
    onClose();
    form.reset();
  }, [onClose, form]);

  const {
    loading,
    invalidEmail,
    openBulkEsignRemindersModal,
    bulkEsignRemindersStatuses,
    handleSubmit,
    closeEmailValidationModal,
    closeBulkEsignRemindersModal,
  } = useUpdatePolicyholder(policyholderLocator, defaultValues, handleClose);

  return (
    <>
      <Modal
        open={open}
        title="Edit contact information"
        onClose={handleClose}
        maxWidth="sm"
        disableClose={loading}>
        <UpdateContactInfoForm
          form={form as UseFormReturn<EditContactForm>}
          onClose={handleClose}
          isNoEmail={defaultValues.noEmail}
          loading={loading}
          onSubmit={handleSubmit}
        />
      </Modal>

      <EmailValidationModal
        email={invalidEmail}
        open={!!invalidEmail}
        onClose={closeEmailValidationModal}
      />

      <BulkEsignRemindersStatusesModal
        open={openBulkEsignRemindersModal}
        onClose={closeBulkEsignRemindersModal}
        statuses={bulkEsignRemindersStatuses}
      />
    </>
  );
}
